/**
 * normalizeImportValues.js
 *
 * Capa de normalizacion para datos crudos de CSV/XLSX antes de enviar al backend.
 * Maneja formatos numericos mixtos (AR/EU/US), simbolos monetarios, caracteres
 * invisibles, espacios no estandar, comillas de CSV y otros artefactos comunes
 * de archivos generados por distintos proveedores.
 *
 * Todo el codigo fuente es ASCII-puro: los Unicode code points se comprueban
 * con charCodeAt() para evitar problemas con ESLint no-irregular-whitespace
 * y no-misleading-character-class.
 */

// -------------------------------------------------
// CONSTANTES
// -------------------------------------------------

/** Valores de celda que representan "sin dato" */
var VACIOS = new Set([
  "", "-", "n/a", "na", "s/d", "sd", "sin dato",
  "null", "none", "undefined", "#n/a",
]);


// -------------------------------------------------
// Helpers de limpieza de caracteres
// -------------------------------------------------

/**
 * Elimina caracteres invisibles de un string.
 * Code points eliminados:
 *   U+00AD  soft hyphen
 *   U+200B  zero-width space
 *   U+200C  zero-width non-joiner
 *   U+200D  zero-width joiner
 *   U+200E  left-to-right mark
 *   U+200F  right-to-left mark
 *   U+202A-U+202E  embedding/override chars
 *   U+2060  word joiner
 *   U+FEFF  BOM / zero-width no-break space
 */
function stripInvisible(s) {
  var out = [];
  for (var i = 0; i < s.length; i++) {
    var c = s.charCodeAt(i);
    if (
      c === 0x00AD ||
      (c >= 0x200B && c <= 0x200F) ||
      (c >= 0x202A && c <= 0x202E) ||
      c === 0x2060 ||
      c === 0xFEFF
    ) {
      continue;
    }
    out.push(s[i]);
  }
  return out.join("");
}

/**
 * Reemplaza espacios no estandar por espacio ASCII normal (U+0020).
 * Code points normalizados:
 *   U+00A0  non-breaking space
 *   U+1680  ogham space mark
 *   U+2000-U+200A  various typographic spaces (en/em/thin/hair/etc)
 *   U+202F  narrow no-break space
 *   U+205F  medium mathematical space
 *   U+3000  ideographic space
 */
function normalizeSpaces(s) {
  var out = [];
  for (var i = 0; i < s.length; i++) {
    var c = s.charCodeAt(i);
    if (
      c === 0x00A0 ||
      c === 0x1680 ||
      (c >= 0x2000 && c <= 0x200A) ||
      c === 0x202F ||
      c === 0x205F ||
      c === 0x3000
    ) {
      out.push(" ");
    } else {
      out.push(s[i]);
    }
  }
  return out.join("");
}

/**
 * Elimina comillas tipograficas y rectas del inicio y fin del string.
 * Code points eliminados al inicio/fin:
 *   U+0022  "  comilla recta doble
 *   U+0027  '  comilla recta simple
 *   U+201C  "  comilla doble izquierda
 *   U+201D  "  comilla doble derecha
 *   U+2018  '  comilla simple izquierda
 *   U+2019  '  comilla simple derecha
 */
function stripQuotes(s) {
  function isQuote(c) {
    return c === 0x0022 || c === 0x0027 ||
           c === 0x201C || c === 0x201D ||
           c === 0x2018 || c === 0x2019;
  }
  var start = 0;
  var end = s.length - 1;
  while (start <= end && isQuote(s.charCodeAt(start))) start++;
  while (end >= start && isQuote(s.charCodeAt(end))) end--;
  return s.slice(start, end + 1);
}

/** Limpieza base aplicada siempre: espacios no estandar, invisibles, trim */
function cleanBase(s) {
  return stripInvisible(normalizeSpaces(s)).trim();
}


// -------------------------------------------------
// parseNumero
// -------------------------------------------------

/**
 * Convierte un valor crudo de celda a numero.
 *
 * Casos manejados:
 *   "1.234,56"  -> 1234.56   (AR/EU: punto=miles, coma=decimal)
 *   "1,234.56"  -> 1234.56   (US: coma=miles, punto=decimal)
 *   "1.234"     -> 1234      (AR: punto como separador de miles)
 *   "1,5"       -> 1.5       (coma como decimal)
 *   "1 234,56"  -> 1234.56   (espacio como separador de miles)
 *   "$85,00"    -> 85        (simbolo monetario al inicio)
 *   "5 %"       -> 5         (porcentaje al final)
 *   "(100)"     -> -100      (formato contable negativo)
 *   85          -> 85        (numero nativo XLSX, pass-through)
 *
 * @param {*}      value
 * @param {object} [opts]
 * @param {number} [opts.min]          Clamping minimo (ej. 0 para precios)
 * @param {number} [opts.max]          Clamping maximo (ej. 100 para descuentos)
 * @param {number} [opts.fallback=NaN] Valor si no se puede parsear
 * @returns {number}
 */
export function parseNumero(value, opts) {
  var min      = opts && opts.min      !== undefined ? opts.min      : undefined;
  var max      = opts && opts.max      !== undefined ? opts.max      : undefined;
  var fallback = opts && opts.fallback !== undefined ? opts.fallback : NaN;

  // 1. Numero nativo (XLSX ya lo parseo)
  if (typeof value === "number") {
    if (!isFinite(value)) return fallback;
    return _clamp(value, min, max);
  }

  // 2. Booleano / null / undefined
  if (value == null || typeof value === "boolean") return fallback;

  // 3. Limpiar
  var s = cleanBase(String(value));

  // 4. Vacios semanticos
  if (VACIOS.has(s.toLowerCase())) return fallback;

  // 5. Quitar comillas literales
  s = stripQuotes(s).trim();

  // 6. Formato contable negativo: (100) -> -100
  var negativo = false;
  if (/^\([\d\s.,]+\)$/.test(s)) {
    negativo = true;
    s = s.slice(1, -1).trim();
  } else if (s.charAt(0) === "-") {
    negativo = true;
    s = s.slice(1).trim();
  }

  // 7. Quitar prefijos de moneda / texto no numerico al inicio
  s = s.replace(/^[^0-9(,.]+/, "").trim();

  // 8. Quitar sufijos de unidad / moneda / porcentaje
  s = s.replace(/[^0-9]+$/, "").trim();

  // 9. Quitar espacios internos (separador de miles: "1 234,56")
  s = s.replace(/\s/g, "");

  if (!s) return fallback;

  // 10. Detectar y normalizar separadores
  var hasDot   = s.indexOf(".") !== -1;
  var hasComma = s.indexOf(",") !== -1;

  if (hasDot && hasComma) {
    // Ambos presentes: el que va ultimo es el decimal
    if (s.lastIndexOf(",") > s.lastIndexOf(".")) {
      // "1.234,56" -> europeo/argentino
      s = s.replace(/\./g, "").replace(",", ".");
    } else {
      // "1,234.56" -> anglosajon
      s = s.replace(/,/g, "");
    }

  } else if (hasComma && !hasDot) {
    var partesC = s.split(",");
    var ultimaC = partesC[partesC.length - 1];
    if (partesC.length === 2 && ultimaC.length === 3 && /^\d+$/.test(partesC[0])) {
      // "1,234" -> miles
      s = s.replace(",", "");
    } else {
      // "1,5" / "1,50" -> decimal
      s = s.replace(",", ".");
    }

  } else if (hasDot && !hasComma) {
    var partesD = s.split(".");
    if (partesD.length > 2) {
      // "1.234.567" -> multiples puntos = todos miles
      s = s.replace(/\./g, "");
    } else if (partesD.length === 2) {
      var entero  = partesD[0];
      var decimal = partesD[1];
      if (decimal.length === 3 && /^\d+$/.test(entero) && entero !== "0") {
        // "1.500" -> 1500 (miles en formato argentino)
        s = s.replace(".", "");
      }
      // else: "1.5", "85.99", "0.500" -> decimal, dejar como esta
    }
  }

  var n = parseFloat(s);
  if (!isFinite(n)) return fallback;
  return _clamp(negativo ? -n : n, min, max);
}


// -------------------------------------------------
// parseTexto
// -------------------------------------------------

/**
 * Limpia un valor de celda para usarlo como string en el backend.
 *
 * - Elimina caracteres invisibles y espacios no estandar
 * - Elimina caracteres de control (tabs, saltos de linea internos)
 * - Quita comillas literales de CSV
 * - Colapsa espacios multiples a uno
 * - Trunca a maxLength (default 500)
 *
 * @param {*}      value
 * @param {object} [opts]
 * @param {number} [opts.maxLength=500]
 * @returns {string}
 */
export function parseTexto(value, opts) {
  var maxLength = opts && opts.maxLength !== undefined ? opts.maxLength : 500;

  if (value == null) return "";

  var s = cleanBase(
    String(value).replace(/[\t\r\n]+/g, " ")
  );

  s = stripQuotes(s).trim();
  s = s.replace(/\s{2,}/g, " ").trim();

  if (s.length > maxLength) s = s.slice(0, maxLength).trim();

  return s;
}


// -------------------------------------------------
// parseCodigoBarras
// -------------------------------------------------

/**
 * Limpia un codigo de barras / codigo de proveedor.
 * XLSX suele convertir codigos numericos a float (ej: 7791234000000 -> 7791234000000.0).
 * Elimina el ".0" espureo que genera esa conversion.
 *
 * @param {*} value
 * @returns {string}
 */
export function parseCodigoBarras(value) {
  if (value == null) return "";

  // XLSX devuelve numeros nativos para celdas numericas
  if (typeof value === "number") {
    return String(Math.round(value));
  }

  var s = parseTexto(value, { maxLength: 100 });

  // Quitar ".0" o ",0" final que XLSX agrega en export CSV
  s = s.replace(/[.,]0+$/, "");

  return s;
}


// -------------------------------------------------
// normalizarFila
// -------------------------------------------------

/**
 * Aplica la normalizacion completa a una fila mapeada antes de enviarla al backend.
 *
 * @param {object} item  Objeto raw con campos crudos del archivo
 * @returns {object}     Objeto con valores normalizados y tipos correctos
 */
export function normalizarFila(item) {
  var out = {};

  // Precio de compra: requerido, float >= 0, truncado a 2 decimales
  out.precioCompra = _truncar2(parseNumero(item.precioCompra, { min: 0, fallback: 0 }));

  // Descuento: float, clampeado a [0, 100], truncado a 2 decimales
  out.descuentoProveedor = _truncar2(parseNumero(item.descuentoProveedor, {
    min: 0,
    max: 100,
    fallback: 0,
  }));

  // Codigo de barras: String! siempre presente (puede ser "")
  out.codigoBarrasProveedor = parseCodigoBarras(item.codigoBarrasProveedor);

  // Nombre: string opcional
  if (item.nombre != null) {
    var nombre = parseTexto(item.nombre);
    if (nombre) out.nombre = nombre;
  }

  // Campos modo ESTRUCTURADO
  var estructuradoKeys = ["marca", "modelo", "tamanio", "otrasCaracteristicas"];
  for (var i = 0; i < estructuradoKeys.length; i++) {
    var k = estructuradoKeys[i];
    if (item[k] != null) {
      var v = parseTexto(item[k]);
      if (v) out[k] = v;
    }
  }

  return out;
}


// -------------------------------------------------
// helpers internos
// -------------------------------------------------

function _clamp(n, min, max) {
  if (min !== undefined && min !== null && n < min) return min;
  if (max !== undefined && max !== null && n > max) return max;
  return n;
}

/** Trunca a 2 decimales sin redondear: 20.049999 -> 20.04, no 20.05 */
function _truncar2(n) {
  return Math.trunc(n * 100) / 100;
}
